import { useTranslation } from '@kostbar/i18n'
import { ImageUploadField } from '../image-upload-field'
import { AdPlaylistEditor } from './ad-playlist-editor'
import { SurfacePreview } from './surface-preview'
import type { BrandingSurface } from '@/api/endpoints/kiosk-branding'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

/**
 * One brandable screen's configuration, next to a live preview of it
 * (CHG-2026-08-kiosk-screen-branding).
 *
 * The enable switch is at the top and drives everything: while it is off the station renders the
 * built-in screen no matter what is filled in below, which is what lets an operator prepare colours
 * and ads, look at the preview, and only then go live.
 */
export function SurfaceEditor({
  surface,
  onChange,
  title,
  description,
  defaultWidth,
  defaultHeight,
  withAttractDelay,
  disabled,
}: {
  surface: BrandingSurface
  onChange: (surface: BrandingSurface) => void
  title: string
  description: string
  defaultWidth: number
  defaultHeight: number
  /** Self-checkout only: the customer display is idle-driven and has no inactivity timer. */
  withAttractDelay?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation('restaurants')

  const patch = (changes: Partial<BrandingSurface>) =>
    onChange({ ...surface, ...changes })
  const patchColors = (changes: Partial<BrandingSurface['colors']>) =>
    onChange({ ...surface, colors: { ...surface.colors, ...changes } })

  const colorField = (
    key: 'background' | 'text' | 'accent',
    label: string,
    fallback: string,
  ) => (
    <div className="space-y-1">
      <Label htmlFor={`color-${key}`}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={`color-${key}`}
          type="color"
          value={surface.colors?.[key] || fallback}
          disabled={disabled}
          onChange={(e) => patchColors({ [key]: e.target.value })}
          className="h-9 w-14 p-1"
        />
        <Input
          value={surface.colors?.[key] ?? ''}
          placeholder={fallback}
          disabled={disabled}
          onChange={(e) => patchColors({ [key]: e.target.value || undefined })}
          className="w-32"
          aria-label={label}
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={surface.enabled}
            disabled={disabled}
            onCheckedChange={(enabled) => patch({ enabled })}
            aria-label={t('branding.enabled')}
          />
          <span className="text-sm">{t('branding.enabled')}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <ImageUploadField
            value={surface.logo}
            onChange={(logo) => patch({ logo })}
            label={t('branding.logo')}
            description={t('branding.logoHint')}
            disabled={disabled}
          />

          <div className="flex flex-wrap gap-4">
            {colorField(
              'background',
              t('branding.colors.background'),
              '#ffffff',
            )}
            {colorField('text', t('branding.colors.text'), '#000000')}
            {colorField('accent', t('branding.colors.accent'), '#318f5a')}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="target-width">{t('branding.target.width')}</Label>
              <Input
                id="target-width"
                type="number"
                min={1}
                value={surface.target?.width ?? defaultWidth}
                disabled={disabled}
                onChange={(e) =>
                  patch({
                    target: {
                      ...surface.target,
                      width: Number(e.target.value) || undefined,
                    },
                  })
                }
                className="w-28"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="target-height">
                {t('branding.target.height')}
              </Label>
              <Input
                id="target-height"
                type="number"
                min={1}
                value={surface.target?.height ?? defaultHeight}
                disabled={disabled}
                onChange={(e) =>
                  patch({
                    target: {
                      ...surface.target,
                      height: Number(e.target.value) || undefined,
                    },
                  })
                }
                className="w-28"
              />
            </div>
            {withAttractDelay && (
              <div className="space-y-1">
                <Label htmlFor="attract-delay">
                  {t('branding.attractDelay')}
                </Label>
                <Input
                  id="attract-delay"
                  type="number"
                  min={10}
                  max={3600}
                  value={surface.attractDelaySeconds ?? 60}
                  disabled={disabled}
                  onChange={(e) =>
                    patch({
                      attractDelaySeconds: Number(e.target.value) || undefined,
                    })
                  }
                  className="w-28"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('branding.ads.title')}</Label>
            <p className="text-muted-foreground text-sm">
              {t('branding.ads.hint')}
            </p>
            <AdPlaylistEditor
              ads={surface.ads ?? []}
              onChange={(ads) => patch({ ads })}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('branding.preview')}</Label>
          <SurfacePreview
            surface={surface}
            fallbackWidth={defaultWidth}
            fallbackHeight={defaultHeight}
            emptyLabel={t('branding.previewEmpty')}
          />
          <p className="text-muted-foreground text-xs">
            {t('branding.previewHint')}
          </p>
        </div>
      </div>
    </div>
  )
}
